// танин баталгаажуулах [emongolia choose]

import useIntl from '@/utils/intl'
import { ArrowForwardIos, MailOutline } from '@mui/icons-material'
import React, { FunctionComponent, useState } from 'react'
import Loader from '@/components/ui/loader'
import Image from 'next/image'
import { sendOtp } from '@/services/mobi-api/confirmation'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

interface Props {
  regno: string
  onContinue: (payload: { channel: 'sms' | 'email' | 'app' }) => void
}

const Step3: FunctionComponent<Props> = ({ regno, onContinue }) => {
  const { translate } = useIntl()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ [key in 'sms' | 'email' | 'app']?: string }>({})

  const sendOtpResponse = async (regno: string, channel: 'sms' | 'email' | 'app'): Promise<void> => {
    try {
      const res = await sendOtp({ regno, channel })
      if (res?.success === false) {
        const msg = res?.result?.error ?? translate('error')
        trackNewNumberEvent('verification_fail', {
          step_name: 'identity_verification',
          method: 'emongolia',
          error_reason: normalizeTrackingText(msg)
        })
        setErrors(prev => ({ ...prev, [channel]: msg }))
        return
      } else {
        onContinue({ channel })
      }
    } catch (error) {
      trackNewNumberEvent('verification_fail', {
        step_name: 'identity_verification',
        method: 'emongolia',
        error_reason: 'otp_send_failed'
      })
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleChoose = async (channel: 'sms' | 'email' | 'app'): Promise<void> => {
    if (loading) return
    trackNewNumberEvent('start_verification', {
      step_name: 'identity_verification',
      method: 'emongolia'
    })
    setLoading(true)
    setErrors(prev => ({ ...prev, [channel]: undefined }))
    await sendOtpResponse(regno, channel)
  }

  const smsHasError = typeof errors.sms === 'string' && errors.sms.length > 0
  // const emailHasError = typeof errors.email === 'string' && errors.email.length > 0
  const appHasError = typeof errors.app === 'string' && errors.app.length > 0

  const asButtonKeydown = (fn: () => void) => (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      fn()
    }
  }

  return (
    <div className='p-8' aria-busy={loading} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {loading && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm'>
          <Loader />
        </div>
      )}

      <div className={`rounded-3xl ${loading ? 'pointer-events-none opacity-60' : ''} space-y-4`}>
        <span className='text-textHigh font-extrabold text-lg'>{translate('verify.confirmation')}</span>

        <div className='flex flex-col bg-surfaceInverse p-4 rounded-2xl gap-3'>
          <div
            onClick={() => { void handleChoose('sms') }}
            onKeyDown={asButtonKeydown(() => { void handleChoose('sms') })}
            className={`md:w-96 p-4 rounded-xl flex flex-col border ${smsHasError ? 'border-red-500' : 'border-secondarySoftLow'
                            }`}
            role='button'
            tabIndex={0}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <div className='border border-secondarySoftLow rounded-lg flex p-2'>
                  <MailOutline
                    sx={{
                      color: 'black',
                      '.dark &': { color: 'white' }
                    }}
                  />
                </div>
                <span className='font-bold text-textHigh'>
                  {translate('verify.emongolia.message')}
                </span>
              </div>
              <ArrowForwardIos fontSize='small' className='text-textHigh' />
            </div>

            {smsHasError && (
              <p className='text-red-500 text-sm mt-2 text-center' role='alert'>{errors.sms}</p>
            )}
          </div>

          <div
            onClick={() => { void handleChoose('app') }}
            onKeyDown={asButtonKeydown(() => { void handleChoose('app') })}
            className={`md:w-96 p-4 rounded-xl flex flex-col border ${appHasError ? 'border-red-500' : 'border-secondarySoftLow'}`}
            role='button'
            tabIndex={0}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <div className='border border-secondarySoftLow rounded-lg flex p-2'>
                  <Image src='/icons/emongolia-svg.svg' width={24} height={24} className='w-6 h-6' alt='mobot' />
                </div>
                <span className='font-bold text-textHigh'>{translate('verify.emongolia.notification')}</span>
              </div>
              <ArrowForwardIos fontSize='small' className='text-textHigh' />
            </div>
            {appHasError && (
              <p className='text-red-500 text-sm mt-1 text-center'>{errors.app}</p>
            )}
          </div>
        </div>
        <div className='flex flex-col bg-surfaceInverse p-4 rounded-xl gap-4 border'>
          <div className='md:w-96 flex flex-col bg-surfaceInverse rounded-xl gap-4'>
            <p className='font-bold text-sm text-textMid'>{translate('verify.recommendation')}</p>
            <p className='text-sm text-textMid'>{translate('verify.recommendation.desc')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Step3
